import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArchiveBoxIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { menuAPI } from '../../services/api'
import { t } from '../../i18n'

// Inventory > Products: stock count of every product with "Track stock" on.
// Restock here; the website shows "Out of stock" at 0 and the owner gets an
// email when a product reaches its alert level.
export default function ProductStockTab({ productsPath = '/admin/products' }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [drafts, setDrafts] = useState({})
  const [saving, setSaving] = useState(null)

  const load = () => menuAPI.getProductStock()
    .then(({ data }) => setItems(data.items || []))
    .catch(() => toast.error(t('Failed to load product stock')))
    .finally(() => setLoading(false))

  useEffect(() => { load() }, [])

  const save = async (item) => {
    const draft = drafts[item.id] || {}
    setSaving(item.id)
    try {
      const { data } = await menuAPI.setProductStock(item.id, {
        stockQuantity: draft.stock ?? item.stock_quantity,
        lowStockThreshold: draft.threshold ?? item.low_stock_threshold,
      })
      setItems(list => list.map(i => (i.id === item.id ? data.item : i)))
      setDrafts(d => { const next = { ...d }; delete next[item.id]; return next })
      toast.success(t('Stock updated'))
    } catch (err) {
      toast.error(t(err.response?.data?.error || 'Failed to update stock'))
    } finally { setSaving(null) }
  }

  const status = item => {
    const qty = Number(item.stock_quantity)
    if (qty <= 0) return { label: 'Out of stock', tone: 'bg-gray-900 text-white' }
    if (qty <= Number(item.low_stock_threshold)) return { label: 'Low stock', tone: 'bg-amber-100 text-amber-800' }
    return { label: 'In stock', tone: 'bg-emerald-100 text-emerald-800' }
  }

  if (loading) return <div className="card"><div className="card-body text-gray-500">{t('Loading...')}</div></div>

  const low = items.filter(i => Number(i.stock_quantity) <= Number(i.low_stock_threshold))

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900">{t('Product stock')}</h3>
        <p className="text-sm text-gray-500">{t('Stock goes down automatically with every order and comes back if an order is cancelled.')}</p>
      </div>
      {low.length > 0 && (
        <div className="mx-6 mt-4 flex gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          {t('{count} products are low or out of stock.', { count: low.length })}
        </div>
      )}
      <div className="card-body p-0">
        {items.length === 0 ? (
          <div className="p-10 text-center">
            <ArchiveBoxIcon className="h-10 w-10 mx-auto text-gray-300" />
            <p className="mt-3 text-gray-600">{t('No products are tracking stock yet.')}</p>
            <p className="text-sm text-gray-500">{t('Edit a product and turn on "Track stock for this item".')}</p>
            <Link to={productsPath} className="btn-primary mt-4 inline-block">{t('Go to products')}</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-6 py-3 text-start font-medium">{t('Product')}</th>
                  <th className="px-6 py-3 text-start font-medium">{t('Status')}</th>
                  <th className="px-6 py-3 text-start font-medium">{t('In stock')}</th>
                  <th className="px-6 py-3 text-start font-medium">{t('Alert at')}</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => {
                  const s = status(item)
                  const draft = drafts[item.id] || {}
                  const dirty = draft.stock !== undefined || draft.threshold !== undefined
                  return (
                    <tr key={item.id}>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          {item.image ? <img src={item.image} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <span className="h-10 w-10 rounded-lg bg-gray-100" />}
                          <div><p className="font-medium text-gray-900">{item.name}</p>{item.sku && <p className="text-xs text-gray-500">{item.sku}</p>}</div>
                        </div>
                      </td>
                      <td className="px-6 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${s.tone}`}>{t(s.label)}</span></td>
                      <td className="px-6 py-3">
                        <input type="number" min="0" step="1" aria-label={t('Quantity in stock for {name}', { name: item.name })} className="form-input w-24"
                          value={draft.stock ?? item.stock_quantity ?? 0} onChange={e => setDrafts(d => ({ ...d, [item.id]: { ...d[item.id], stock: e.target.value } }))} />
                      </td>
                      <td className="px-6 py-3">
                        <input type="number" min="0" step="1" aria-label={t('Low stock alert for {name}', { name: item.name })} className="form-input w-20"
                          value={draft.threshold ?? item.low_stock_threshold ?? 5} onChange={e => setDrafts(d => ({ ...d, [item.id]: { ...d[item.id], threshold: e.target.value } }))} />
                      </td>
                      <td className="px-6 py-3 text-end">
                        <button onClick={() => save(item)} disabled={!dirty || saving === item.id} className="btn-primary btn-sm disabled:opacity-40">
                          {saving === item.id ? t('Saving...') : t('Save')}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
